"use client";

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2,
  Phone,
  Clock,
  MessageSquare,
  CheckCircle,
  XCircle,
  ChevronRight,
  Send,
  Volume2,
  Square,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

interface RecentCall {
  id: string;
  originalId: number;
  conversation_id: string;
  external_number: string;
  status: string;
  duration_seconds: number | null;
  created_at: string;
  audio_url?: string | null;
  // Feedback info
  feedbackId?: string;
  feedbackStatus?: "pending" | "in_progress" | "resolved";
  feedbackText?: string;
}

interface AgentRecentCallsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  agentName: string;
  onFeedbackAdded?: () => void;
}

export function AgentRecentCallsDialog({
  open,
  onOpenChange,
  agentId,
  agentName,
  onFeedbackAdded,
}: AgentRecentCallsDialogProps) {
  const { fetchWithAuth } = useAuth();
  const [calls, setCalls] = useState<RecentCall[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [playingCallId, setPlayingCallId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (open) {
      loadRecentCalls();
    } else {
      setCalls([]);
      setExpandedCallId(null);
      setFeedbackText("");
      // Stop audio when dialog closes
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingCallId(null);
    }
  }, [open, agentId]);

  const handlePlayAudio = async (call: RecentCall, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!call.audio_url) return;

    // If same call is playing, stop it
    if (playingCallId === call.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingCallId(null);
      return;
    }

    // Stop previous audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Resolve storage:// URLs to signed URLs
    let audioUrl = call.audio_url;
    if (audioUrl.startsWith('storage://')) {
      try {
        const response = await fetchWithAuth(`/api/calls/${call.originalId}/recording`);
        if (!response.ok) {
          throw new Error('Failed to get recording URL');
        }
        const data = await response.json();
        audioUrl = data.url;
      } catch (err) {
        toast.error("Failed to load recording");
        return;
      }
    }

    // Create and play new audio
    const audio = new Audio(audioUrl);
    audio.onended = () => {
      setPlayingCallId(null);
      audioRef.current = null;
    };
    audio.onerror = () => {
      toast.error("Failed to play audio");
      setPlayingCallId(null);
      audioRef.current = null;
    };

    audioRef.current = audio;
    audio.play();
    setPlayingCallId(call.id);
  };

  const loadRecentCalls = async () => {
    try {
      setIsLoading(true);

      // Load recent calls for this agent
      const callsRes = await fetchWithAuth(
        `/api/calls?agentId=${agentId}&pageSize=5&source=voice_agent`
      );
      const callsData = await callsRes.json();

      const recentCalls: RecentCall[] = (callsData.calls || []).map((call: any) => ({
        id: call.id,
        originalId: call.originalId,
        conversation_id: call.voiceAgentData?.conversationId || "",
        external_number: call.callerNumber || call.calledNumber || "Unknown",
        status: call.status,
        duration_seconds: call.durationSeconds,
        created_at: call.createdAt,
        audio_url: call.voiceAgentData?.audioUrl || null,
      }));

      // Load feedback statuses for these calls
      if (recentCalls.length > 0) {
        const feedbackRes = await fetchWithAuth(
          `/api/ai/feedback?agent_type=voice_agent&agent_id=${agentId}&limit=50`
        );
        const feedbackData = await feedbackRes.json();

        // Map feedback to calls
        const feedbackMap = new Map<string, { id: string; status: string; text: string }>();
        (feedbackData.items || []).forEach((fb: any) => {
          feedbackMap.set(fb.reference_id, { id: fb.id, status: fb.status, text: fb.feedback_text || "" });
        });

        recentCalls.forEach((call) => {
          const fb = feedbackMap.get(call.conversation_id);
          if (fb) {
            call.feedbackId = fb.id;
            call.feedbackStatus = fb.status as any;
            call.feedbackText = fb.text;
          }
        });
      }

      setCalls(recentCalls);
    } catch (error) {
      console.error("Failed to load recent calls:", error);
      toast.error("Failed to load recent calls");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFeedback = async (call: RecentCall) => {
    if (!feedbackText.trim()) return;

    try {
      setIsSaving(true);

      const res = await fetchWithAuth("/api/ai/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reference_type: "voice_response",
          reference_id: call.conversation_id,
          feedback_text: feedbackText.trim(),
          agent_id: agentId,
          agent_type: "voice_agent",
        }),
      });

      if (!res.ok) throw new Error("Failed to add feedback");

      toast.success("Feedback added");
      setFeedbackText("");
      setExpandedCallId(null);
      onFeedbackAdded?.();

      // Reload to show updated feedback status
      loadRecentCalls();
    } catch (error) {
      console.error("Failed to add feedback:", error);
      toast.error("Failed to add feedback");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateFeedback = async (call: RecentCall) => {
    if (!feedbackText.trim() || !call.feedbackId) return;

    try {
      setIsSaving(true);

      const res = await fetchWithAuth(`/api/ai/feedback/${call.feedbackId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          feedback_text: feedbackText.trim(),
        }),
      });

      if (!res.ok) throw new Error("Failed to update feedback");

      toast.success("Feedback updated");
      setFeedbackText("");
      setExpandedCallId(null);
      onFeedbackAdded?.();

      // Reload to show updated feedback
      loadRecentCalls();
    } catch (error) {
      console.error("Failed to update feedback:", error);
      toast.error("Failed to update feedback");
    } finally {
      setIsSaving(false);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return "Just now";
  };

  const getStatusIcon = (status: string) => {
    if (status === "done" || status === "completed") {
      return <CheckCircle className="w-3 h-3 text-green-500" />;
    }
    return <XCircle className="w-3 h-3 text-red-500" />;
  };

  const getFeedbackBadge = (status?: string) => {
    if (!status) return null;

    const colors: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-700",
      in_progress: "bg-blue-100 text-blue-700",
      resolved: "bg-green-100 text-green-700",
    };

    return (
      <Badge className={`text-xs ${colors[status] || ""}`}>
        {status.replace("_", " ")}
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            <span>Recent Calls -</span> {agentName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : calls.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No recent calls</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {calls.map((call) => (
              <div
                key={call.id}
                className="border rounded-lg overflow-hidden"
              >
                {/* Call row */}
                <div
                  className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    if (expandedCallId === call.id) {
                      setExpandedCallId(null);
                      setFeedbackText("");
                    } else {
                      setExpandedCallId(call.id);
                      // Pre-fill with existing feedback text if pending
                      if (call.feedbackStatus === "pending" && call.feedbackText) {
                        setFeedbackText(call.feedbackText);
                      } else {
                        setFeedbackText("");
                      }
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(call.status)}
                    <div>
                      <div className="text-sm font-medium">
                        <span className="text-gray-400 font-normal">from </span>
                        {call.external_number}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatDuration(call.duration_seconds)}
                        <span className="text-gray-300">|</span>
                        {formatDate(call.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {call.audio_url && (
                      <button
                        onClick={(e) => handlePlayAudio(call, e)}
                        className={`p-1.5 rounded-full transition-colors ${
                          playingCallId === call.id
                            ? "bg-blue-100 text-blue-600"
                            : "hover:bg-gray-100 text-gray-500"
                        }`}
                        title={playingCallId === call.id ? "Stop" : "Play recording"}
                      >
                        {playingCallId === call.id ? (
                          <Square className="w-3.5 h-3.5" />
                        ) : (
                          <Volume2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                    {call.feedbackStatus ? (
                      getFeedbackBadge(call.feedbackStatus)
                    ) : (
                      <MessageSquare className="w-4 h-4 text-gray-300" />
                    )}
                    <ChevronRight
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        expandedCallId === call.id ? "rotate-90" : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Expanded feedback input - show for new or pending feedback */}
                {expandedCallId === call.id && (!call.feedbackId || call.feedbackStatus === "pending") && (
                  <div className="p-3 pt-0 border-t bg-gray-50">
                    <Textarea
                      placeholder={call.feedbackId ? "Edit feedback..." : "Add feedback for this call..."}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      rows={2}
                      className="text-sm resize-none mb-2"
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => call.feedbackId ? handleUpdateFeedback(call) : handleAddFeedback(call)}
                        disabled={isSaving || !feedbackText.trim()}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        {isSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-3 h-3 mr-1" />
                            {call.feedbackId ? "Update Feedback" : "Add Feedback"}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Show existing feedback info for in_progress and resolved */}
                {expandedCallId === call.id && call.feedbackId && call.feedbackStatus !== "pending" && (
                  <div className="p-3 pt-0 border-t bg-gray-50">
                    <p className="text-sm text-gray-600">
                      <span>Feedback is</span> <span>{call.feedbackStatus === "in_progress" ? "in progress" : "resolved"}</span> <span>and cannot be edited.</span>
                    </p>
                    {call.feedbackText && (
                      <p className="text-sm text-gray-500 mt-1 italic">
                        <span>&quot;</span>{call.feedbackText}<span>&quot;</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
